/**
 * /influencer-link — primary route for feature `influencer-link`.
 *
 * Mounts the existing `InfluencerLinkPage` from
 * `@/features/influencer-link/`. A sibling namespaced route
 * (`/influencers/link`) also exists and wraps the same page inside a
 * breadcrumb/heading shell; this primary route exposes the feature page
 * directly so that dash-contract-check rule D1 (route file at
 * `routes/<slug>.tsx`) is satisfied and the feature is discoverable at
 * `/influencer-link`.
 *
 * The page accepts an optional `initialInfluencerId` prop; we pull it from
 * the `?influencerId=` search param so deep-links from other surfaces keep
 * working.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { InfluencerLinkPage } from "@/features/influencer-link/InfluencerLinkPage";

interface InfluencerLinkSearch {
	influencerId?: string;
}

export const influencerLinkRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/influencer-link",
	validateSearch: (raw: Record<string, unknown>): InfluencerLinkSearch => {
		const influencerId = raw.influencerId;
		if (typeof influencerId === "string" && influencerId.trim().length > 0) {
			return { influencerId: influencerId.trim() };
		}
		return {};
	},
	component: InfluencerLinkRoutePage,
});

export default influencerLinkRoute;

function InfluencerLinkRoutePage() {
	const { influencerId } = influencerLinkRoute.useSearch();
	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10">
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
					Influencer tracking
				</p>
				<h1 className="text-green-700 font-semibold text-3xl tracking-tight">
					Influencer links
				</h1>
			</header>
			<InfluencerLinkPage initialInfluencerId={influencerId} />
		</main>
	);
}
